import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription, forkJoin, of } from 'rxjs';
import { ICrossFieldRuleSpec } from '../../../../shared/models/MetadataSchema';
import { DialogService } from '../../../../shared/services/dialog.service';
import { fieldTypeLabel } from '../../../../shared/utils/field-type-label';
import { describeCrossFieldRuleForLeftTemplate } from '../../../../shared/utils/requirements-renderer';
import { MetadataMgmtFacade } from '../../../metadata-mgmt/facade/metadata-mgmt.facade';
import { MetadataMgmtService } from '../../../metadata-mgmt/service/metadata-mgmt.service';
import { IMetadataKeyList } from '../../../metadata-mgmt/models/Metadata-mgmt';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import {
  ICrossFieldRule,
  ICrossFieldRuleCreate,
  IRuleParamDefinition,
  IRuleTypeCatalogEntry,
  IValidatorRule,
  IValidatorRuleCreate,
  RuleSeverity,
  WidgetType,
} from '../../models/validation-rule';

/**
 * Mode the modal is operating in. `null` => closed.
 *   - `field` + null rule  => create field rule
 *   - `field` + rule       => edit field rule
 *   - `cross` + null rule  => create cross-field rule
 *   - `cross` + rule       => edit cross-field rule
 */
export type RuleEditorMode =
  | { kind: 'field'; rule: IValidatorRule | null }
  | { kind: 'cross'; rule: ICrossFieldRule | null }
  | null;

const SEVERITY_OPTIONS: RuleSeverity[] = ['ERROR', 'WARNING'];

/**
 * Rule editor whose params are fully schema-driven by the catalog endpoint
 * (`GET /api/metadata-validations/rule-types/`). Every param carries a
 * `widget_type`, `choices`, `default_value`, `is_required` field that
 * tells the template which control to render and how to validate it.
 *
 * Previously this component had a hardcoded `controlKindFor()` switch
 * and per-vocab constants (FORMAT_OPTIONS, OP_OPTIONS, ...). Those moved
 * into the database; the form now reads them directly from the catalog
 * entry's `param_defs`, so backend-side schema changes propagate to the
 * UI without code changes.
 */
@Component({
  selector: 'app-rule-editor-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './rule-editor-modal.component.html',
})
export class RuleEditorModalComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() modalId!: string;
  @Input() mode: RuleEditorMode = null;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dialog') dialogEl!: ElementRef<HTMLDialogElement>;

  private fb = inject(FormBuilder);
  private facade = inject(ValidationRulesFacade);
  private metadataFacade = inject(MetadataMgmtFacade);
  private metadataService = inject(MetadataMgmtService);
  private dialog = inject(DialogService);

  catalog = this.facade.catalogSignal;

  // Templates for the field-rule picker. Loaded on init for the
  // currently-selected source type; ng-select renders each row as
  // "name (key_name · source)".
  templatesSignal = this.metadataFacade.metadataKeysSignal;

  // Source types for the cross-field picker. Loaded on init.
  sourceTypesSignal = this.metadataFacade.sourceTypesSignal;

  /**
   * Templates available to the cross-field editor. Cross-field rules
   * can reference both Core templates and templates from a specific
   * source type, so this is the UNION of (Core, source-type-specific).
   * Refetched when the form's `source_type` changes.
   */
  crossFieldTemplatesSignal = signal<IMetadataKeyList[]>([]);
  private crossTemplateSub: Subscription | null = null;

  // Mirror `mode` (a plain @Input, non-reactive) into a signal so the
  // catalog filter and any other computed signal recomputes whenever
  // the modal is reopened in a different kind. Without this, opening
  // the editor in `field` mode after `cross` (or vice-versa) leaves
  // `scopedCatalog` stale because the underlying `catalog()` hasn't
  // changed.
  private modeSignal = signal<RuleEditorMode>(null);

  /** Catalog entries scoped to the kind currently being edited. */
  scopedCatalog = computed<IRuleTypeCatalogEntry[]>(() => {
    const kind = this.modeSignal()?.kind;
    if (!kind) return [];
    return this.catalog().filter((e) => e.kind === kind);
  });

  // Reactive `metadata_template` value, surfaced for the template-bound
  // param widgets and for the prune-on-template-change effect below.
  metadataTemplateValueSignal = signal<number | null>(null);
  // Cross-field analogues; populated only when the modal is in cross mode.
  leftTemplateValueSignal = signal<number | null>(null);
  rightTemplateValueSignal = signal<number | null>(null);

  /** The currently-picked field template, or null if none chosen. */
  selectedTemplate = computed<IMetadataKeyList | null>(() => {
    const tid = this.metadataTemplateValueSignal();
    if (tid === null || tid === undefined) return null;
    return this.templatesSignal().find((t) => t.id === tid) ?? null;
  });

  /** Cross-field rule's currently-picked left template, or null. */
  leftSelectedTemplate = computed<IMetadataKeyList | null>(() => {
    const tid = this.leftTemplateValueSignal();
    if (tid === null || tid === undefined) return null;
    return this.crossFieldTemplatesSignal().find((t) => t.id === tid) ?? null;
  });

  /** Cross-field rule's currently-picked right template, or null. */
  rightSelectedTemplate = computed<IMetadataKeyList | null>(() => {
    const tid = this.rightTemplateValueSignal();
    if (tid === null || tid === undefined) return null;
    return this.crossFieldTemplatesSignal().find((t) => t.id === tid) ?? null;
  });

  /**
   * Return the template whose `options` back a TEMPLATE_OPTION_* widget
   * — or `null` for non-template-bound widget types.
   */
  templateForWidget(widget: WidgetType): IMetadataKeyList | null {
    switch (widget) {
      case 'TEMPLATE_OPTION_SELECT':
      case 'TEMPLATE_OPTION_MULTI_SELECT':
        return this.selectedTemplate();
      case 'LEFT_TEMPLATE_OPTION_SELECT':
      case 'LEFT_TEMPLATE_OPTION_MULTI_SELECT':
        return this.leftSelectedTemplate();
      case 'RIGHT_TEMPLATE_OPTION_SELECT':
      case 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT':
        return this.rightSelectedTemplate();
      default:
        return null;
    }
  }

  /**
   * True iff the relevant template has any pickable options. Drives the
   * dropdown-vs-free-input fork: SELECT-typed templates expose
   * `options` so the picker can show a dropdown; TEXT/NUMBER/DATE/GPS
   * templates carry no options and the editor falls back to a free
   * text / tag input.
   */
  hasTemplateOptions(widget: WidgetType): boolean {
    return (this.templateForWidget(widget)?.options?.length ?? 0) > 0;
  }

  /**
   * SELECT and BOOLEAN params are visually elevated to a 2-column
   * "Operators" row at the top of the params section so single-token
   * choices (operators, scopes, format keys) don't get buried among
   * multi-line tag/text inputs.
   */
  operatorParamDefs = computed<IRuleParamDefinition[]>(() =>
    this.paramDefsSignal().filter(
      (d) => d.widget_type === 'SELECT' || d.widget_type === 'BOOLEAN',
    ),
  );
  otherParamDefs = computed<IRuleParamDefinition[]>(() =>
    this.paramDefsSignal().filter(
      (d) => d.widget_type !== 'SELECT' && d.widget_type !== 'BOOLEAN',
    ),
  );

  // Form holds common fields up front; dynamic param controls are appended
  // to `form.controls.params` (a nested FormGroup) by rebuildParamControls().
  form!: FormGroup;
  // Ordered list of param definitions for the currently-selected rule
  // type. The template iterates over this directly; each entry carries
  // widget_type / choices / help_text / default_value so the form needs
  // zero knowledge of param-name semantics.
  paramDefsSignal = signal<IRuleParamDefinition[]>([]);
  serverErrorsSignal = signal<string[]>([]);

  readonly SEVERITY_OPTIONS = SEVERITY_OPTIONS;

  // Exposed to the template so each picker can show the selected field's
  // data type ("Date", "Number", "Select", ...) without a back-and-forth
  // trip to the metadata view.
  readonly fieldTypeLabel = fieldTypeLabel;

  constructor() {
    // Late-catalog-load fallback: the catalog is fetched async on page
    // init, so if the user opens the modal and picks a rule type before
    // the response lands, the first `rebuildParamControls` call sees an
    // empty catalog and produces no params. Re-run the rebuild whenever
    // the catalog updates, but only when the form already has a chosen
    // rule type AND we don't yet have a schema in `paramDefsSignal` —
    // that guard avoids clobbering orphan-decorated edits and any
    // schema already produced by an earlier successful rebuild.
    effect(() => {
      const catalog = this.catalog();
      if (catalog.length === 0) return;
      if (!this.form) return;
      const rt = this.form.get('rule_type')?.value as string | undefined;
      if (!rt) return;
      if (this.paramDefsSignal().length > 0) return;
      this.rebuildParamControls(rt);
    });

    // Prune template-bound param values that are no longer valid when
    // the user swaps templates. Keeps the form coherent without forcing
    // a full param-controls rebuild. Covers field-rule (single template)
    // and cross-field-rule (left + right) variants.
    effect(() => {
      const fieldTemplate = this.selectedTemplate();
      const leftTemplate = this.leftSelectedTemplate();
      const rightTemplate = this.rightSelectedTemplate();
      if (!this.form) return;
      const paramsGroup = this.form.get('params');
      if (!paramsGroup) return;

      const validsFor = (widget: WidgetType): Set<string> => {
        switch (widget) {
          case 'TEMPLATE_OPTION_SELECT':
          case 'TEMPLATE_OPTION_MULTI_SELECT':
            return new Set((fieldTemplate?.options ?? []).map((o) => o.value));
          case 'LEFT_TEMPLATE_OPTION_SELECT':
          case 'LEFT_TEMPLATE_OPTION_MULTI_SELECT':
            return new Set((leftTemplate?.options ?? []).map((o) => o.value));
          case 'RIGHT_TEMPLATE_OPTION_SELECT':
          case 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT':
            return new Set((rightTemplate?.options ?? []).map((o) => o.value));
          default:
            return new Set();
        }
      };

      const SINGLE: ReadonlySet<WidgetType> = new Set<WidgetType>([
        'TEMPLATE_OPTION_SELECT',
        'LEFT_TEMPLATE_OPTION_SELECT',
        'RIGHT_TEMPLATE_OPTION_SELECT',
      ]);
      const MULTI: ReadonlySet<WidgetType> = new Set<WidgetType>([
        'TEMPLATE_OPTION_MULTI_SELECT',
        'LEFT_TEMPLATE_OPTION_MULTI_SELECT',
        'RIGHT_TEMPLATE_OPTION_MULTI_SELECT',
      ]);

      for (const def of this.paramDefsSignal()) {
        const isSingle = SINGLE.has(def.widget_type);
        const isMulti = MULTI.has(def.widget_type);
        if (!isSingle && !isMulti) continue;
        const control = paramsGroup.get(def.key);
        if (!control) continue;
        const valid = validsFor(def.widget_type);
        // When the bound template has no options (e.g. a TEXT template),
        // the editor renders a free-text / tag input instead of a
        // dropdown. Pruning by "must be in options" would wipe out
        // legitimate free-form values, so skip the pruning step.
        if (valid.size === 0) continue;
        const v = control.value;
        if (isSingle && v && !valid.has(v)) {
          control.setValue(null, { emitEvent: false });
        } else if (isMulti && Array.isArray(v)) {
          const filtered = v.filter((x) => valid.has(x));
          if (filtered.length !== v.length) control.setValue(filtered, { emitEvent: false });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.modalId && this.dialogEl?.nativeElement) {
      this.dialog.register(this.modalId, this.dialogEl.nativeElement);
    }
    // Ensure the template list is populated for the picker. The
    // MetadataMgmtFacade is shared, so re-dispatching is cheap and the
    // signal will just keep the same data when already-loaded.
    const sid = this.facade.selectedSourceTypeIdSignal();
    // Scope the picker to the active kind so a post-analysis rule only offers
    // post-analysis templates (and vice versa) — the engine never evaluates a
    // cross-field rule whose two templates differ in kind.
    this.metadataFacade.getMetadataKeys(sid ?? undefined, this.facade.selectedKindSignal());
    // Cross-field editor also needs the list of source types for its
    // Source Type dropdown.
    this.metadataFacade.getSourceTypes();
    this.initForm();
  }

  ngOnDestroy(): void {
    if (this.modalId) this.dialog.unregister(this.modalId);
    this.crossTemplateSub?.unsubscribe();
  }

  /**
   * Reload the cross-field template options when the user changes the
   * Source Type. Always include Core templates plus, when a specific
   * source type is picked, that type's templates too. Bypasses the
   * shared store so the field-rule picker doesn't get clobbered.
   */
  private reloadCrossFieldTemplates(sourceTypeId: number | null | undefined): void {
    this.crossTemplateSub?.unsubscribe();
    // Both sides of a cross-field rule must share the active kind, otherwise the
    // kind-scoped engine silently skips the rule.
    const kind = this.facade.selectedKindSignal();
    const coreReq = this.metadataService.getMetadataKey(undefined, kind);
    const sourceReq =
      sourceTypeId === null || sourceTypeId === undefined
        ? of<IMetadataKeyList[]>([])
        : this.metadataService.getMetadataKey(sourceTypeId, kind);
    this.crossTemplateSub = forkJoin([coreReq, sourceReq]).subscribe({
      next: ([core, scoped]) => {
        // Dedupe defensively in case the backend ever returns a
        // template under both queries; keep the first occurrence.
        const seen = new Set<number>();
        const merged: IMetadataKeyList[] = [];
        for (const t of [...(core ?? []), ...(scoped ?? [])]) {
          if (seen.has(t.id)) continue;
          seen.add(t.id);
          merged.push(t);
        }
        this.crossFieldTemplatesSignal.set(merged);
      },
      error: () => this.crossFieldTemplatesSignal.set([]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['mode']) return;
    // Keep the reactive mirror in sync with the @Input so `scopedCatalog`
    // and any other computed signal recomputes when the modal is reopened
    // in a different kind.
    this.modeSignal.set(this.mode);
    // The field-rule picker reads the SHARED metadata-keys store, which is
    // only loaded once in ngAfterViewInit. The sidebar's source-type switch
    // refreshes the template-list panel's own local list but leaves the
    // shared store stale, so reopening the modal would otherwise show the
    // previously-selected source type's fields. Refetch for the currently-
    // selected source type every time the modal opens in field mode.
    if (this.mode?.kind === 'field') {
      const sid = this.facade.selectedSourceTypeIdSignal();
      this.metadataFacade.getMetadataKeys(sid ?? undefined, this.facade.selectedKindSignal());
    }
    if (this.form) this.initForm();
  }

  // ---- form lifecycle -------------------------------------------------

  private initForm(): void {
    const mode = this.mode;
    this.serverErrorsSignal.set([]);

    if (!mode) {
      // Closed: build a minimal empty form so the template can bind safely.
      this.form = this.fb.group({
        rule_type: [''],
        severity: ['ERROR'],
        message_template: [''],
        rule_group: [''],
        sort_order: [0],
        is_active: [true],
        params: this.fb.group({}),
        // Cross-field-only fields. Disabled when not in `cross` mode.
        metadata_template: [null],
        left_template: [null],
        right_template: [null],
        source_type: [null],
      });
      this.paramDefsSignal.set([]);
      return;
    }

    const isEdit = mode.rule !== null;
    const isField = mode.kind === 'field';
    const initial = mode.rule;

    this.form = this.fb.group({
      rule_type: [initial?.rule_type ?? '', Validators.required],
      severity: [(initial?.severity as RuleSeverity) ?? 'ERROR', Validators.required],
      message_template: [initial?.message_template ?? ''],
      rule_group: [isField ? (initial as IValidatorRule | null)?.rule_group ?? '' : ''],
      sort_order: [initial?.sort_order ?? 0],
      is_active: [initial?.is_active ?? true],
      params: this.fb.group({}),
      // template/source linkage
      metadata_template: [
        isField ? (initial as IValidatorRule | null)?.metadata_template ?? null : null,
        isField ? Validators.required : null,
      ],
      left_template: [
        !isField ? (initial as ICrossFieldRule | null)?.left_template ?? null : null,
        !isField ? Validators.required : null,
      ],
      right_template: [
        !isField ? (initial as ICrossFieldRule | null)?.right_template ?? null : null,
        !isField ? Validators.required : null,
      ],
      source_type: [
        !isField ? (initial as ICrossFieldRule | null)?.source_type ?? null : null,
      ],
    });

    // Pre-populate the relevant template fields from the rule-by-template
    // panel's current selection so the admin doesn't have to re-pick the
    // template they were just looking at. For field rules: seed
    // `metadata_template`. For cross-field rules: seed `source_type` so
    // the Left/Right Template lists come pre-scoped, and seed
    // `left_template` (conventionally the "primary" template) so the
    // panel's template ends up on the left side. Right Template is
    // intentionally left blank — the admin must explicitly pick the
    // other side of the comparison.
    if (!isEdit) {
      const tid = this.facade.selectedTemplateIdSignal();
      const sid = this.facade.selectedSourceTypeIdSignal();
      if (isField) {
        if (tid !== null) this.form.patchValue({ metadata_template: tid });
      } else {
        const patch: Record<string, number | null> = {};
        if (sid !== null) patch['source_type'] = sid;
        if (tid !== null) patch['left_template'] = tid;
        if (Object.keys(patch).length > 0) this.form.patchValue(patch);
      }
    }

    // Mirror metadata_template into a signal so the template-bound
    // value pickers and the prune effect react to picker changes.
    this.metadataTemplateValueSignal.set(this.form.value.metadata_template ?? null);
    this.form
      .get('metadata_template')
      ?.valueChanges.subscribe((v: number | null) =>
        this.metadataTemplateValueSignal.set(v ?? null),
      );

    // Cross-field-only: load the available templates for the current
    // source-type (Core + scoped) and refresh whenever the picker
    // changes. Left/Right Template dropdowns iterate this signal, and
    // their picked values drive the per-side template-bound value
    // pickers.
    if (!isField) {
      this.reloadCrossFieldTemplates(this.form.value.source_type ?? null);
      this.form
        .get('source_type')
        ?.valueChanges.subscribe((v: number | null) =>
          this.reloadCrossFieldTemplates(v ?? null),
        );

      this.leftTemplateValueSignal.set(this.form.value.left_template ?? null);
      this.rightTemplateValueSignal.set(this.form.value.right_template ?? null);
      this.form
        .get('left_template')
        ?.valueChanges.subscribe((v: number | null) =>
          this.leftTemplateValueSignal.set(v ?? null),
        );
      this.form
        .get('right_template')
        ?.valueChanges.subscribe((v: number | null) =>
          this.rightTemplateValueSignal.set(v ?? null),
        );
    }

    // Rebuild params when rule_type changes.
    this.form.get('rule_type')?.valueChanges.subscribe((rt) => this.rebuildParamControls(rt));
    if (this.form.value.rule_type) {
      this.rebuildParamControls(this.form.value.rule_type, initial?.params ?? {});
    }
  }

  /**
   * Replace `form.params` with controls described by the catalog entry's
   * `param_defs`. Each param's widget_type/choices/default come straight
   * from the DB row so the form's shape tracks server-side schema edits
   * without code changes.
   *
   * Params absent from the catalog but present on an existing rule are
   * still rendered (as TEXT) so admins can clean them up. Params present
   * in the catalog but absent from the existing rule fall back to the
   * `default_value`.
   */
  private rebuildParamControls(ruleType: string, existing: Record<string, unknown> = {}): void {
    // `rule_type` codes are unique within a kind but can be shared across
    // kinds (e.g. ``MUTUALLY_EXCLUSIVE_VALUE`` exists for both field and
    // cross with different param schemas). Pin the catalog lookup to the
    // current mode's kind so the wrong schema can't leak in.
    const kind = this.modeSignal()?.kind;
    const entry = this.catalog().find(
      (e) => e.rule_type === ruleType && (!kind || e.kind === kind),
    );
    const defs = [...(entry?.param_defs ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key),
    );
    const known = new Set(defs.map((d) => d.key));

    // Synthesize a TEXT param def for any incoming key the catalog
    // doesn't know about, so the admin can see/edit/remove the orphaned
    // value rather than having it silently disappear from the form.
    const orphanDefs: IRuleParamDefinition[] = Object.keys(existing)
      .filter((k) => !known.has(k))
      .map((k) => ({
        key: k,
        label: k,
        help_text: 'Param not present in the current catalog (orphan).',
        widget_type: 'TEXT',
        is_required: false,
        default_value: null,
        choices: [],
        sort_order: 999,
      }));

    const allDefs = [...defs, ...orphanDefs];

    const paramsGroup = this.fb.group({});
    for (const def of allDefs) {
      const raw = def.key in existing ? existing[def.key] : def.default_value;
      paramsGroup.addControl(
        def.key,
        this.fb.control(
          this.normalizeIncoming(def, raw),
          def.is_required ? Validators.required : null,
        ),
      );
    }

    this.form.setControl('params', paramsGroup);
    this.paramDefsSignal.set(allDefs);
  }

  /**
   * Normalize an incoming value to a shape the configured widget accepts.
   * Mostly a no-op except for list-shaped widgets (TAGS / MULTI_SELECT),
   * which always want an array.
   */
  private normalizeIncoming(def: IRuleParamDefinition, value: unknown): unknown {
    if (
      def.widget_type === 'TAGS' ||
      def.widget_type === 'MULTI_SELECT' ||
      def.widget_type === 'TEMPLATE_OPTION_MULTI_SELECT' ||
      def.widget_type === 'LEFT_TEMPLATE_OPTION_MULTI_SELECT' ||
      def.widget_type === 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT'
    ) {
      if (Array.isArray(value)) return value;
      if (value === undefined || value === null || value === '') return [];
      return [value];
    }
    if (def.widget_type === 'BOOLEAN') {
      return value === undefined || value === null ? false : Boolean(value);
    }
    return value ?? '';
  }

  // ---- client-side validation -------------------------------------

  formLevelErrors(): string[] {
    if (!this.form) return [];
    const errs: string[] = [];
    const ruleType = this.form.value.rule_type as string;
    const params = (this.form.value.params ?? {}) as Record<string, unknown>;

    if (ruleType === 'NUMERIC_RANGE') {
      const min = params['min'];
      const max = params['max'];
      if (min !== null && min !== '' && max !== null && max !== '') {
        if (Number(min) > Number(max)) errs.push('min must be <= max');
      }
    }
    if (ruleType === 'REGEX') {
      const pattern = String(params['pattern'] ?? '');
      if (pattern) {
        try {
          new RegExp(pattern);
        } catch (e) {
          errs.push(`Invalid regex: ${(e as Error).message}`);
        }
      }
    }
    return errs;
  }

  // ---- submit / close -------------------------------------------------

  isCreate(): boolean {
    return this.mode !== null && this.mode.rule === null;
  }

  close(): void {
    this.closed.emit();
  }

  submit(): void {
    if (!this.mode || !this.form) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.formLevelErrors().length > 0) return;

    const raw = this.form.value;
    const params = this.normalizeOutgoingParams(raw.params ?? {});
    const common = {
      rule_type: raw.rule_type,
      severity: raw.severity,
      message_template: raw.message_template ?? '',
      is_active: !!raw.is_active,
      sort_order: Number(raw.sort_order ?? 0),
      params,
    };

    if (this.mode.kind === 'field') {
      const payload: IValidatorRuleCreate = {
        ...common,
        metadata_template: Number(raw.metadata_template),
        rule_group: raw.rule_group ?? '',
      };
      if (this.mode.rule) {
        this.facade.patchFieldRule(this.mode.rule.id, payload);
      } else {
        this.facade.createFieldRule(payload);
      }
    } else {
      const payload: ICrossFieldRuleCreate = {
        ...common,
        left_template: Number(raw.left_template),
        right_template: Number(raw.right_template),
        source_type: raw.source_type === null || raw.source_type === '' ? null : Number(raw.source_type),
      };
      if (this.mode.rule) {
        this.facade.patchCrossFieldRule(this.mode.rule.id, payload);
      } else {
        this.facade.createCrossFieldRule(payload);
      }
    }

    this.close();
  }

  /**
   * Convert form-shaped params back to the JSON shape the backend wants.
   * Widget type comes from the per-param definition, so coercion (Number
   * vs Boolean vs Array) is driven by DB config rather than param names.
   */
  private normalizeOutgoingParams(raw: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const defsByKey = new Map(this.paramDefsSignal().map((d) => [d.key, d]));
    for (const [k, v] of Object.entries(raw)) {
      if (v === '' || v === null || v === undefined) continue;
      const def = defsByKey.get(k);
      const widget: WidgetType = def?.widget_type ?? 'TEXT';
      if (widget === 'NUMBER') {
        out[k] = Number(v);
      } else if (widget === 'BOOLEAN') {
        out[k] = !!v;
      } else if (
        widget === 'TAGS' ||
        widget === 'MULTI_SELECT' ||
        widget === 'TEMPLATE_OPTION_MULTI_SELECT' ||
        widget === 'LEFT_TEMPLATE_OPTION_MULTI_SELECT' ||
        widget === 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT'
      ) {
        if (Array.isArray(v) && v.length > 0) out[k] = v;
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  // ---- template helpers ----------------------------------------------

  getControlErrors(name: string): ValidationErrors | null {
    return this.form?.get(name)?.errors ?? null;
  }

  isCrossField(): boolean {
    return this.mode?.kind === 'cross';
  }

  /**
   * Plain-English preview of the cross-field rule as currently configured,
   * e.g. "Date Sequenced — Must be on or after Date Collected". Reuses the
   * exact phrasing the requirement hints show end users, so authors see the
   * effect of their Dependent/Governing choice live. Returns '' until enough
   * of the form is filled in to describe anything.
   *
   * Reads `form.value` directly (rather than a computed signal) so it tracks
   * rule_type + params edits under default change detection without rewiring
   * the params FormGroup's subscriptions on every rule-type switch.
   */
  crossFieldPreview(): string {
    if (!this.isCrossField() || !this.form) return '';
    const ruleType = this.form.get('rule_type')?.value as string | undefined;
    const left = this.leftSelectedTemplate();
    const right = this.rightSelectedTemplate();
    if (!ruleType || !left || !right) return '';

    const spec: ICrossFieldRuleSpec = {
      rule_type: ruleType,
      params: (this.form.get('params')?.value ?? {}) as Record<string, unknown>,
      severity: (this.form.get('severity')?.value as 'ERROR' | 'WARNING') ?? 'ERROR',
      left_template_id: left.id,
      right_template_id: right.id,
      left_template_name: left.name,
      right_template_name: right.name,
    };
    const phrase = describeCrossFieldRuleForLeftTemplate(spec, left.id);
    return `${left.name} — ${phrase}`;
  }
}
